import { LexicalEditor } from "lexical";
import React, { createContext, useContext, useMemo, useReducer } from "react";

export type OnScreenEditor = {
  id: string;
  location: "sidebar" | "main";
  editor: LexicalEditor;
};

export type EditorsOnScreenAction =
  | ({ type: "add" } & OnScreenEditor)
  | { type: "remove"; id: string };

interface OnScreenEditorsContextType {
  onScreenEditors: OnScreenEditor[];
  dispatchOnScreenEditors: (action: EditorsOnScreenAction) => void;
}

const OnScreenEditorsContext = createContext<
  OnScreenEditorsContextType | undefined
>(undefined);

function onScreenEditorsReducer(
  state: OnScreenEditor[],
  action: EditorsOnScreenAction,
): OnScreenEditor[] {
  switch (action.type) {
    case "add": {
      const newEditor: OnScreenEditor = {
        id: action.id,
        editor: action.editor,
        location: action.location,
      };
      return [...state.filter((e) => e.id !== action.id), newEditor];
    }
    case "remove":
      return state.filter((e) => e.id !== action.id);
    default:
      return state;
  }
}

export const OnScreenEditorsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [onScreenEditors, dispatchOnScreenEditors] = useReducer(
    onScreenEditorsReducer,
    [],
  );

  const value = useMemo(
    () => ({ onScreenEditors, dispatchOnScreenEditors }),
    [onScreenEditors, dispatchOnScreenEditors],
  );

  return (
    <OnScreenEditorsContext.Provider value={value}>
      {children}
    </OnScreenEditorsContext.Provider>
  );
};

export function useOnScreenEditorsContext() {
  const context = useContext(OnScreenEditorsContext);
  if (!context) {
    throw new Error(
      "useOnScreenEditorsContext must be used within an OnScreenEditorsProvider",
    );
  }
  return context;
}
